import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Box, Stack, Step, StepConnector, StepLabel, Stepper, Typography } from '@mui/material';
import { Check, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import TransactionStatus from '../../../buyFunded/components/TransactionStatus/TransactionStatus';
import { getTransactionStatus } from '../../../../services/transaction/transaction';
import { TransactionStatusBackend, UIStatus } from '../../../buyFunded/contents/PaymentStatusStep/types';

interface WithdrawStatusStepProps {
  transactionExternalId: string | null;
  onSuccess?: () => void;
}

const STATUS_UI: Record<UIStatus, { labelKey: string; color: string; icon: React.ReactNode }> = {
  DONE: { labelKey: 'STATUSES.COMPLETED', color: '#354EEF', icon: <Check size={16} color="#fff" /> },
  WAITING: { labelKey: 'STATUSES.STILL_IN_PROGRESS', color: '#F5A623', icon: <Clock size={16} color="#fff" /> },
  ERROR: { labelKey: 'STATUSES.SOMETHING_WENT_WRONG', color: '#E74C3C', icon: <X size={16} color="#fff" /> },
};

const StepIcon = ({ status }: { status: UIStatus }) => (
  <Box
    sx={{
      width: 24, height: 24, borderRadius: '50%',
      bgcolor: STATUS_UI[status].color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {STATUS_UI[status].icon}
  </Box>
);

const Connector = ({ done }: { done: boolean }) => (
  <StepConnector
    sx={{
      '& .MuiStepConnector-line': {
        borderColor: done ? '#354EEF' : '#2A2B33',
        borderLeftWidth: 2,
        minHeight: 24,
      },
    }}
  />
);

const WithdrawStatusStep: React.FC<WithdrawStatusStepProps> = ({ transactionExternalId, onSuccess }) => {
  const { t, i18n } = useTranslation('paymentModal');

  const [status, setStatus] = useState<TransactionStatusBackend>('PENDING');
  const cancelledRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!transactionExternalId) {
      setStatus('FAILED');
      return;
    }

    cancelledRef.current = false;

    const poll = () => {
      getTransactionStatus(transactionExternalId).then((res) => {
        if (cancelledRef.current) return;

        if (!res) {
          setStatus('FAILED');
          return;
        }

        setStatus(res.status as TransactionStatusBackend);

        if (res.status === 'PENDING' || res.status === 'PAYOUT_EXECUTED') {
          setTimeout(poll, 1000);
        }
      });
    };

    poll();

    return () => {
      cancelledRef.current = true;
    };
  }, [transactionExternalId, i18n.language, t]);

  useEffect(() => {
    if (status === 'COMPLETED' && onSuccessRef.current) {
      onSuccessRef.current();
    }
  }, [status]);

  const isCompleted = status === 'COMPLETED';
  const isPending = status === 'PENDING' || status === 'PAYOUT_EXECUTED';
  const isFailed = ['FAILED', 'CANCELLED', 'ERROR', 'EXPIRED', 'REJECTED'].includes(status);

  const steps: { title: string; uiStatus: UIStatus }[] = isFailed
    ? [{ title: t('PAYMENT_STATUSES.PAYMENT_INITIATED'), uiStatus: 'ERROR' }]
    : isCompleted
    ? [
        { title: t('PAYMENT_STATUSES.PAYMENT_INITIATED'), uiStatus: 'DONE' },
        { title: t('STATUSES.PROCESSING'), uiStatus: 'DONE' },
        { title: t('STATUSES.COMPLETED'), uiStatus: 'DONE' },
      ]
    : isPending
    ? [
        { title: t('PAYMENT_STATUSES.PAYMENT_INITIATED'), uiStatus: 'DONE' },
        { title: t('STATUSES.PROCESSING'), uiStatus: 'WAITING' },
      ]
    : [
        { title: t('PAYMENT_STATUSES.PAYMENT_INITIATED'), uiStatus: 'DONE' },
        { title: t('STATUSES.PROCESSING'), uiStatus: 'DONE' },
        { title: t('PAYMENT_STATUSES.PAYMENT_DECLINED'), uiStatus: 'ERROR' },
      ];

  return (
    <Stack gap={4}>
      <Stepper orientation="vertical" connector={null}>
        {steps.map((s, i) => (
          <Fragment key={s.title}>
            {i > 0 && <Connector done={steps[i - 1].uiStatus === 'DONE'} />}
            <Step active>
              <StepLabel StepIconComponent={() => <StepIcon status={s.uiStatus} />}>
                <Typography>{s.title}</Typography>
                <Typography variant="body2" sx={{ color: STATUS_UI[s.uiStatus].color }}>
                  {t(STATUS_UI[s.uiStatus].labelKey)}
                </Typography>
              </StepLabel>
            </Step>
          </Fragment>
        ))}
      </Stepper>

      <TransactionStatus status={status} />
    </Stack>
  );
};

export default WithdrawStatusStep;
